import React, { useEffect, useRef, useState } from 'react';
import { Field, LockableField, AddressFields, EMPTY_ADDR, cleanName, isValidName, NAME_MAX } from './fields';
import { loadPlaces, parsePlace, regionFromPostcode, normalizePostcode } from './places';
import { meter, flushMeter } from './gmaps-meter';
import { lookupAfm } from './aade';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};
const GR_STATES = cfg.grStates && typeof cfg.grStates === 'object' ? cfg.grStates : {};

// Address editor in a popup: the address form (incl. phone) + a Google map with
// a draggable pin. In invoice mode it also carries the ΑΦΜ (AADE lookup) and the
// fetched company details.
export default function AddressModal({ title, initial, onSave, onClose, invoiceMode, invoice, showPhone = false }) {
  const [draft, setDraft] = useState({ ...EMPTY_ADDR, ...(initial || {}) });
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const [inv, setInv] = useState({ ...(invoice || {}) });
  const [afm, setAfm] = useState((invoice && invoice.billing_vat) || '');
  const [afmStatus, setAfmStatus] = useState(invoice && invoice.billing_company ? 'valid' : 'idle');
  const [afmMsg, setAfmMsg] = useState('');
  // AADE-autofilled company fields start locked; the pencil unlocks them.
  const [locks, setLocks] = useState({ billing_company: !!(invoice && invoice.billing_company), billing_irs: !!(invoice && invoice.billing_company) });
  const unlock = (k) => setLocks((l) => ({ ...l, [k]: false }));
  const locksRef = useRef(locks);
  locksRef.current = locks;
  const lastSent = useRef((invoice && invoice.billing_vat ? invoice.billing_vat.replace(/\D/g, '').slice(0, 9) : ''));

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const skipForward = useRef(false);
  const lastQuery = useRef('');
  const debounce = useRef(null);

  // Detail fields + map stay hidden until an address is chosen (autocomplete,
  // AADE fill, or an existing address being edited).
  const [revealed, setRevealed] = useState(() => !!(draft.postcode || draft.city || draft.state));
  useEffect(() => {
    if (draft.postcode || draft.city || draft.state) setRevealed(true);
  }, [draft.postcode, draft.city, draft.state]);

  // Required-field validation runs on Save (not silently disabling the button),
  // so the user gets feedback on what's missing.
  const [invalids, setInvalids] = useState({});
  const [saveMsg, setSaveMsg] = useState('');
  const numDigits = (s) => ((s || '').match(/\d/g) || []).length;
  function handleSave() {
    const hasAddress = (draft.address_1 || '').trim() !== '';
    // Always-visible required fields.
    const e = {};
    if (!isValidName(draft.first_name)) e.first_name = true;
    if (!isValidName(draft.last_name)) e.last_name = true;
    if (!hasAddress) e.address_1 = true;
    if (showPhone && numDigits(draft.phone) < 10) e.phone = true; // shipping only
    if (invoiceMode && !(inv.billing_company || '').trim()) e.afm = true;
    // Postcode/city only matter once revealed (i.e. an address is chosen).
    if (revealed) {
      if (numDigits(draft.postcode) < 4) e.postcode = true;
      if (!(draft.city || '').trim()) e.city = true;
    }
    setInvalids(e);
    if (Object.keys(e).length) {
      setSaveMsg('Συμπλήρωσε σωστά όλα τα υποχρεωτικά πεδία.');
      return;
    }
    // Name + address ok but the address was typed without picking a suggestion
    // (details still hidden) — reveal them so the rest can be completed, rather
    // than saving a half-empty address.
    if (!revealed) {
      setRevealed(true);
      setSaveMsg('Συμπλήρωσε τα υπόλοιπα στοιχεία της διεύθυνσης.');
      return;
    }
    // Pass the ΑΦΜ lookup status so the parent can mark the invoice AADE-verified
    // (drives 39Α eligibility + island VAT — a valid lookup is the trust signal).
    onSave(draft, invoiceMode ? inv : null, invoiceMode ? afmStatus : null);
  }
  // Clear flags once the user starts fixing things.
  useEffect(() => { setInvalids({}); setSaveMsg(''); }, [draft, inv]);

  // Escape closes the modal.
  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // AADE lookup (invoice mode).
  const digits = (afm.match(/\d/g) || []).join('').slice(0, 9);
  useEffect(() => {
    if (!invoiceMode) return undefined;
    if (digits.length !== 9) {
      if (afmStatus !== 'idle' && afmStatus !== 'loading') setAfmStatus('idle');
      if (!digits) setAfmMsg('');
      return undefined;
    }
    if (digits === lastSent.current) return undefined;
    lastSent.current = digits;
    let cancelled = false;
    setAfmStatus('loading');
    setAfmMsg('');
    lookupAfm(digits).then((r) => {
      if (cancelled) return;
      if (r.status === 'valid') {
        setAfmStatus('valid');
        setInv({ billing_company: r.billing.billing_company, billing_vat: digits, billing_irs: r.billing.billing_irs, billing_store: r.billing.billing_store });
        setDraft((d) => ({
          ...d,
          company: r.billing.billing_company,
          address_1: r.billing.billing_address_1 || d.address_1,
          city: r.billing.billing_city || d.city,
          postcode: normalizePostcode(r.billing.billing_postcode) || d.postcode,
          // ΑΑΔΕ gives no region — derive Περιφέρεια from the fetched ΤΚ.
          state: regionFromPostcode(r.billing.billing_postcode) || d.state,
        }));
        // Lock the fetched fields (grey + pencil); only lock an address field
        // the ΑΑΔΕ actually returned, so blanks stay freely editable.
        setLocks({
          billing_company: true,
          billing_irs: true,
          address_1: !!r.billing.billing_address_1,
          postcode: !!r.billing.billing_postcode,
          city: !!r.billing.billing_city,
        });
      } else {
        setAfmStatus(r.status);
        setAfmMsg(
          r.status === 'inactive' ? r.message
          : r.status === 'notfound' ? 'Το ΑΦΜ δεν βρέθηκε στο μητρώο της ΑΑΔΕ.'
          : 'Η υπηρεσία της ΑΑΔΕ δεν είναι διαθέσιμη αυτή τη στιγμή. Δοκίμασε ξανά σε λίγο.'
        );
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits, invoiceMode]);

  function forwardGeocode() {
    const d = draftRef.current;
    const q = [d.address_1, d.postcode, d.city, 'Ελλάδα'].filter(Boolean).join(', ');
    if (!geocoderRef.current || !q || q === lastQuery.current) return;
    lastQuery.current = q;
    meter('geocoding');
    geocoderRef.current.geocode({ address: q, region: 'gr' }, (res, status) => {
      if (status === 'OK' && res && res[0] && mapRef.current && markerRef.current) {
        const loc = res[0].geometry.location;
        mapRef.current.setCenter(loc);
        mapRef.current.setZoom(16);
        markerRef.current.setPosition(loc);
      }
    });
  }

  useEffect(() => {
    if (!revealed || mapRef.current) return undefined; // map mounts only once revealed
    let cancelled = false;
    loadPlaces().then(() => {
      if (cancelled || !window.google || !mapEl.current || mapRef.current) return;
      const g = window.google.maps;
      geocoderRef.current = new g.Geocoder();
      // Autocomplete already handed us the coordinates inside the Place Details
      // response, so open straight on the address instead of geocoding it again.
      const d = draftRef.current;
      const hasCoords = typeof d.lat === 'number' && typeof d.lng === 'number';
      const center = hasCoords ? { lat: d.lat, lng: d.lng } : { lat: 37.9838, lng: 23.7275 };
      meter('dynamic_map');
      const map = new g.Map(mapEl.current, { center, zoom: hasCoords ? 17 : 12, disableDefaultUI: true, zoomControl: true, mapTypeControl: false, streetViewControl: false, fullscreenControl: false });
      const marker = new g.Marker({ map, position: center, draggable: true });
      mapRef.current = map;
      markerRef.current = marker;
      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        const lat = pos.lat();
        const lng = pos.lng();
        meter('geocoding');
        geocoderRef.current.geocode({ location: pos }, (res, status) => {
          skipForward.current = true;
          if (status === 'OK' && res && res[0]) {
            const parsed = parsePlace(res[0], GR_STATES);
            setDraft((d) => {
              const lk = locksRef.current || {};
              const next = { ...d, ...parsed, lat, lng };
              // Dragging the pin is an explicit correction — the shopper is
              // saying "the address is wrong, it's here" — so the pin wins over
              // the text. Only an AADE-locked field outranks it, since there the
              // company seat is authoritative and the map is not.
              if (lk.address_1) next.address_1 = d.address_1;
              else next.address_1 = parsed.address_1 || res[0].formatted_address || d.address_1 || '';
              if (lk.postcode) next.postcode = d.postcode;
              if (lk.city) next.city = d.city;
              return next;
            });
          } else setDraft((d) => ({ ...d, lat, lng }));
        });
      });
      // Only when autocomplete gave us nothing to centre on (hand-typed address,
      // or one restored from the address book before coordinates were stored).
      if (!hasCoords) forwardGeocode();
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  // Keep the pin following the address as it's edited by hand. lastQuery inside
  // forwardGeocode drops repeats, and skipForward stops a pin drag from bouncing
  // the map back to where the text says it should be.
  useEffect(() => {
    if (skipForward.current) { skipForward.current = false; return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(forwardGeocode, 700);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.address_1, draft.postcode, draft.city]);

  // One usage tally per address-entry session, saved or abandoned.
  useEffect(() => () => flushMeter('closed'), []);

  // Invoice popup: keep it to just the ΑΦΜ field until a full ΑΦΜ is entered (or was
  // already resolved). The company (Επωνυμία/ΔΟΥ) + address + map stay collapsed so
  // the user isn't faced with a wall of empty fields before giving a ΑΦΜ.
  const showInvoiceFields = !invoiceMode || afmStatus === 'valid' || digits.length === 9;

  return (
    <div className="cn-modal-overlay" onClick={onClose}>
      <div className="cn-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cn-modal__head">
          <span className="cn-modal__title">{title}</span>
          <button type="button" className="cn-modal__close" onClick={onClose} aria-label="Κλείσιμο">✕</button>
        </div>
        <div className="cn-modal__body">
          {invoiceMode && (
            <div className="cn-grid">
              <Field label="Όνομα" value={draft.first_name} onChange={(val) => setDraft((d) => ({ ...d, first_name: cleanName(val) }))} maxLength={NAME_MAX} autoComplete="given-name" placeholder="π.χ. Γιάννης" valid={isValidName(draft.first_name)} invalid={invalids.first_name} />
              <Field label="Επίθετο" value={draft.last_name} onChange={(val) => setDraft((d) => ({ ...d, last_name: cleanName(val) }))} maxLength={NAME_MAX} autoComplete="family-name" placeholder="π.χ. Παπαδόπουλος" valid={isValidName(draft.last_name)} invalid={invalids.last_name} />
            </div>
          )}
          {invoiceMode && (
            <div className="cn-afm-block">
              <label className="cn-field cn-field--full">
                <span className="cn-field__label">ΑΦΜ</span>
                <div className={`cn-afm-input ${afmStatus === 'valid' ? 'is-valid' : ''} ${(afmStatus === 'inactive' || afmStatus === 'notfound' || afmStatus === 'error' || invalids.afm) ? 'is-invalid' : ''}`}>
                  <input type="text" inputMode="numeric" maxLength={12} value={afm} placeholder="π.χ. 123456789" onChange={(e) => setAfm(e.target.value)} />
                  {afmStatus === 'loading' && <span className="cn-afm-spinner" aria-label="Έλεγχος…" />}
                  {afmStatus === 'valid' && <span className="cn-afm-check" aria-hidden="true">✓</span>}
                </div>
              </label>
              {afmMsg && <p className="cn-afm-error" role="alert">{afmMsg}</p>}
              {showInvoiceFields && (
                <div className="cn-afm-company">
                  <LockableField label="Επωνυμία" value={inv.billing_company} onChange={(v) => setInv((x) => ({ ...x, billing_company: v }))} locked={locks.billing_company} onUnlock={() => unlock('billing_company')} full valid={(inv.billing_company || '').trim() !== ''} />
                  <LockableField label="ΔΟΥ" value={inv.billing_irs} onChange={(v) => setInv((x) => ({ ...x, billing_irs: v }))} locked={locks.billing_irs} onUnlock={() => unlock('billing_irs')} full valid={(inv.billing_irs || '').trim() !== ''} />
                </div>
              )}
            </div>
          )}
          {showInvoiceFields && (
            <AddressFields value={draft} onChange={setDraft} locks={invoiceMode ? locks : undefined} onUnlock={unlock} revealed={revealed} onReveal={() => setRevealed(true)} invalids={invalids} showPhone={showPhone} showName={!invoiceMode} />
          )}
          {/* No map/pin for company invoices — the seat address is authoritative
              from ΑΑΔΕ, so pinpointing a location isn't applicable. */}
          {!invoiceMode && revealed && (
            <div className="cn-map-wrap">
              <span className="cn-map-hint"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '4px' }} aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>Σύρε την πινέζα για ακριβή θέση</span>
              <div ref={mapEl} className="cn-map" />
            </div>
          )}
        </div>
        {saveMsg && <p className="cn-afm-error cn-modal__err" role="alert">{saveMsg}</p>}
        <div className="cn-modal__foot">
          <button type="button" className="cn-btn cn-btn--ghost" onClick={onClose}>Ακύρωση</button>
          <button type="button" className="cn-btn cn-btn--primary" onClick={handleSave}>Αποθήκευση</button>
        </div>
      </div>
    </div>
  );
}
